// ============================================================================
// MarketChatPage — чат по заявке (штатный мессенджер платформы).
// Обсуждение — механизм ядра getOrCreateContextThread (один чат на объект:
// context_type=document, context_code=mkt_requests, context_id=id заявки).
// Если у заявки уже есть мастер и его карточка привязана к пользователю, мастер
// добавляется в обсуждение существующим RPC (идемпотентно). Ошибка доступа к
// карточке мастера НЕ скрывается: показывается предупреждение над лентой.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatThread } from '@/components/chat/ChatThread';
import { addConversationMember, getOrCreateContextThread } from '@/lib/chatBridge';
import { useAuthStore } from '@/store/authStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { marketRequestTitle, type MarketRequest } from '@/lib/market';
import { loadMarketMasterEntityId } from '@/lib/marketActions';
import { MarketError } from '@/components/market/MarketState';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { loadMarketDisputeSummary, type DisputeStatus } from '@/lib/marketDispute';

interface MarketChatPageProps {
  request: MarketRequest;
  onBack: () => void;
  /** Переход к спору по заявке (история/открытие). */
  onOpenDispute?: (request: MarketRequest) => void;
}

export function MarketChatPage({ request, onBack, onOpenDispute }: MarketChatPageProps) {
  const { t } = useI18n();
  const myId = String(useAuthStore((s) => s.user?.id || ''));
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const [conversationId, setConversationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [disputeStatus, setDisputeStatus] = useState<DisputeStatus>('none');
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setWarning('');
    try {
      const title = marketRequestTitle(request);
      const thread = await getOrCreateContextThread({
        objectType: 'document',
        objectCode: 'mkt_requests',
        objectId: request.id,
        title,
      });
      setConversationId(thread.conversationId);

      // Участники обсуждения заявки (существующий RPC; повтор идемпотентен):
      //  • ЗАКАЗЧИК (created_by) — чтобы мастер и клиент видели друг друга.
      //    При открытии клиентом это он сам (myId) — не добавляем;
      //  • МАСТЕР (master_id) — при открытии заказчиком.
      // Если взял ТЕКУЩИЙ пользователь (master_id = я), добавлять некого:
      // карточка mkt_masters к нему может быть не привязана, и предупреждение
      // «не привязана» было бы ложным.
      const warnings: string[] = [];
      if (request.createdBy && request.createdBy !== myId) {
        try {
          await addConversationMember(thread.conversationId, request.createdBy);
        } catch (e) {
          logErrorToJournal(e, 'Витрина маркетплейса: добавление заказчика в обсуждение');
          warnings.push(`Заказчик не добавлен в чат автоматически: ${friendlyError(e)}`);
        }
      }
      if (request.masterId && request.masterId !== myId) {
        try {
          const masterUserId = await loadMarketMasterEntityId(request.masterId);
          if (!masterUserId) {
            warnings.push('Карточка мастера не привязана к учётной записи — пригласите его вручную.');
          } else if (masterUserId !== myId) {
            await addConversationMember(thread.conversationId, masterUserId);
          }
        } catch (e) {
          logErrorToJournal(e, 'Витрина маркетплейса: добавление мастера в обсуждение');
          warnings.push(`Мастер не добавлен в чат автоматически: ${friendlyError(e)}`);
        }
      }
      if (warnings.length > 0) setWarning(warnings.join(' '));
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: открытие чата заявки');
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [myId, request]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  useEffect(() => {
    let cancelled = false;
    loadMarketDisputeSummary(request.id)
      .then((state) => { if (!cancelled) setDisputeStatus(state.status); })
      .catch((e) => logErrorToJournal(e, 'Витрина маркетплейса: состояние спора'));
    return () => { cancelled = true; };
  }, [request.id]);

  if (error) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar
          title={t('market.chat')}
          subtitle={request.categoryName || marketRequestTitle(request)}
          onBack={onBack}
        />
        <div className="mx-auto w-full max-w-2xl p-4 sm:p-6">
          <MarketError failure={{ kind: 'other', message: error }} onRetry={() => setTick((n) => n + 1)} />
        </div>
      </div>
    );
  }

  if (loading || !conversationId) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar
          title={t('market.chat')}
          subtitle={request.categoryName || marketRequestTitle(request)}
          onBack={onBack}
        />
        <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Открываем обсуждение…
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {warning && (
        <div className="shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-800">
          <div className="mx-auto flex w-full max-w-2xl items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{warning}</span>
          </div>
        </div>
      )}
      {onOpenDispute && (
        <div className="shrink-0 border-b bg-background px-3 py-1.5">
          <div className="mx-auto flex w-full max-w-2xl items-center gap-2">
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-muted-foreground">
              <Scale className="h-3.5 w-3.5 shrink-0" />
              {disputeStatus === 'open'
                ? 'Спор открыт — идёт разбор оператором'
                : disputeStatus === 'closed'
                  ? 'Спор решён — история сохранена'
                  : 'Спор по заявке не открыт'}
            </span>
            <Button
              type="button"
              variant={disputeStatus === 'open' ? 'default' : 'outline'}
              size="sm"
              className="h-8 shrink-0 rounded-lg px-2.5 text-xs"
              onClick={() => onOpenDispute(request)}
            >
              {disputeStatus === 'none' ? 'Открыть спор' : 'Спор'}
            </Button>
          </div>
        </div>
      )}
      <div className="min-h-0 flex-1">
        <ChatThread
          key={conversationId}
          conversationId={conversationId}
          title={marketRequestTitle(request)}
          contextType="document"
          contextCode="mkt_requests"
          contextId={request.id}
          myId={myId}
          isAdmin={isAdmin}
          onBack={onBack}
          onConversationLeft={onBack}
        />
      </div>
    </div>
  );
}
